import { useEffect, useState } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { Footer } from './Footer';
import { ErrorBoundary } from './ErrorBoundary';
import { VerifyBanner } from './VerifyBanner';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { categoryMeta } from '../lib/categories';

function usePageMeta(): { title: string; breadcrumb: string } {
  const { pathname } = useLocation();
  const params = useParams();

  if (pathname === '/' || pathname.startsWith('/overview')) return { title: 'Overview', breadcrumb: 'Workspace · Overview' };
  if (pathname.startsWith('/explore')) return { title: 'Explore Data', breadcrumb: 'Workspace · Explore' };
  if (pathname.startsWith('/compare')) return { title: 'Country Comparison', breadcrumb: 'Global · Comparison' };
  if (pathname.startsWith('/heatmap')) return { title: 'ESG Heatmap', breadcrumb: 'Global · Heatmap' };
  if (pathname.startsWith('/watchlist')) return { title: 'Watchlist', breadcrumb: 'Workspace · Watchlist' };
  if (pathname.startsWith('/dashboards')) return { title: 'Dashboards', breadcrumb: 'Workspace · Dashboards' };
  if (pathname.startsWith('/account')) return { title: 'Account & API', breadcrumb: 'Account · Settings' };
  if (pathname.startsWith('/privacy')) return { title: 'Privacy Policy', breadcrumb: 'Legal · Privacy' };
  if (pathname.startsWith('/terms')) return { title: 'Terms & Conditions', breadcrumb: 'Legal · Terms' };
  if (pathname.startsWith('/contact')) return { title: 'Contact', breadcrumb: 'Company · Contact' };
  if (pathname.startsWith('/faq')) return { title: 'FAQ', breadcrumb: 'Company · FAQ' };
  if (pathname.startsWith('/category/')) {
    const meta = categoryMeta(params.category ?? '');
    return { title: meta?.label ?? 'Indicators', breadcrumb: `${meta?.group ?? 'Data'} · ${meta?.label ?? ''}` };
  }
  return { title: 'ESGDash', breadcrumb: '' };
}

export function Layout() {
  const { title, breadcrumb } = usePageMeta();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  useDocumentTitle(title);

  // Close the mobile drawer on navigation.
  useEffect(() => setMobileOpen(false), [pathname]);

  return (
    <div className="app-shell">
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <div className={`sidebar-wrap ${mobileOpen ? 'open' : ''}`}>
        <Sidebar onNavigate={() => setMobileOpen(false)} />
      </div>
      {mobileOpen && <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />}
      <div className="main-content">
        <TopBar title={title} breadcrumb={breadcrumb} onMenu={() => setMobileOpen((o) => !o)} />
        <VerifyBanner />
        <main className="page" id="main">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
        <Footer />
      </div>
    </div>
  );
}
